import moment from "moment";
import Vue from "vue";
import { BooleanHelper } from "~/helpers/booleanHelper";
import { FlashMode } from "~/helpers/flashHelper";
import Guid from '~/helpers/guid';
import { ITrackedModel } from "~/interfaces/modelInterfaces";
import { IChanges } from '~/interfaces/undoRedoInterfaces';
import { Logger } from '~/services/logger';
import TrackedArrayProxy from "../../../formability/form_builder/models/trackedArrayProxy";
import { Constructor } from "../../../global/constructor";
import { ISortable } from "../../interfaces/propertyInterfaces";
import { IPeekerOptions } from "../../services/peeker";
import store from "../../state/store";
import Model from "../model";
import { RelationType } from "../relationType";
import {IOrganization} from '~/interfaces/organizationInterfaces'

export default class TrackedModel extends Model implements ITrackedModel {
  _changes: any;
  _hasChanged: boolean = false;
  _ignoreChanges: boolean = false;
  _triggerChanges: boolean = true;
  _defaultExcept: string[] = ['except', 'rootModel', 'store', 'u_at', 'hasChanged', 'changes', 'parent', 'saveType', 'lastSavedMessage', 'createEmbeddedObject', 'JSON_IGNORE', 'afterPlace'];
  _nameInParent: string;
  _track: boolean = true;

  rootModel
  except: string[] = ['createEmbeddedObject', 'organization_id'];
  organization_id: string;

  _organization: IOrganization;
  set organization(val: IOrganization) {
    this._organization = val;
  }

  /**
   * root-level models may need to override this
   */
  get organization(): IOrganization {
    if (this._organization) {
      return this._organization;
    } else {
      return this.parent.organization;
    }
  }

  //Autosave concerns
  saveTimer: number;
  saveType: string = '';
  lastSavedMessage: string = '';
  updateLastSavedMessageTimer: any;
  u_at_moment: moment.Moment;
  c_at: string;
  u_at: string;
  afterPlace = {}

  constructor(options?: {_track?: boolean, triggerChanges?: boolean}) {
    // build models using new Model().loadFromJson(json)
    // so the entire model is loaded before data is introduced
    // avoids parent model defaults from overriding child model defaults
    super();
    this._changes = {};
    if (BooleanHelper.isStrictBoolean(options?._track)) {
      this._track = options._track
    }
    if (BooleanHelper.isStrictBoolean(options?.triggerChanges)) {
      this.triggerChanges = options.triggerChanges
    }
  }

  get UNTRACKED_PROPERTIES(): string[] {
    return [];
  }

  get EMBEDDED_HASHES(): string[] {
    return [];
  }

  get TRACKED_ARRAYS(): string[] {
    return [];
  }

  get JSON_IGNORE(): string[] {
    return [];
  }

  loadFromJson(json, root: Model = null, options: any = {}): this {

    // add empty json for tracked array properties
    // so they get initialized even if the back end
    // doesn't know about them yet
    if (this.TRACKED_ARRAYS?.length) {
      this.TRACKED_ARRAYS.forEach(key => {
        if (json && !json[key]) json[key] = this[key] || []
      })
    }

    super.loadFromJson(json, root, options);
    if (this.hasChanged && Object.keys(this.changes).length === 0) {
      this.hasChanged = false;
    }
    return this;
  }

  protected loadProperty(keyName: string, json: any, options: any = {}): any {
    if (this.hasOwnProperty(keyName)) {
      const underlyingKeyNameVar = '_' + keyName;
      if (this[underlyingKeyNameVar] !== json[keyName]) {
        if (this.TRACKED_ARRAYS.includes(keyName)) {
          this[underlyingKeyNameVar] = TrackedModel.createTrackedArrayProxy(keyName, json[keyName], this);
        } else {
          this[underlyingKeyNameVar] = json[keyName];
        }
      }
    } else {
      (() => {
        const key = keyName;

        if (this.TRACKED_ARRAYS.includes(key)) {
          this.defineProperty(key, TrackedModel.createTrackedArrayProxy(keyName, json[key], this));
        } else {
          this.defineProperty(key, json[key]);
        }
      }).bind(this)();
    }
  }

  defineProperty<T>(key: keyof T, initialValue: any, trackInitialValue: boolean = false, beforeSetCallback: (model: T, oldVal, newVal) => void = null, afterSetCallback: (model: T, oldVal, newVal) => void = null) {
    const underlyingVar = '_' + key;

    Object.defineProperty(this, key, {
      get() {
        return this[underlyingVar];
      },

      set(newVal) {
        const currVal = this[underlyingVar];
        if (currVal === newVal) {
          return;
        }

        // make sure a TrackedArrayProxy is always set for these keys, not a plain array
        if (this.TRACKED_ARRAYS.includes(key) && Array.isArray(newVal)) {
          newVal = TrackedModel.createTrackedArrayProxy(key, newVal, this)
        }

        if (beforeSetCallback) {
          beforeSetCallback(this, this[underlyingVar], newVal);
        }
        this[underlyingVar] = newVal;
        if (afterSetCallback) {
          afterSetCallback(this, this[underlyingVar], newVal);
        }

        if (!this.UNTRACKED_PROPERTIES.includes(key) && this._track !== false) {
          if (this.parent &&
              this.parent.changes._destroy &&
              this.parent.changes._destroy[this.client_id]) {
            this.updateCreationDeletionIfNecessary();
          } else {
            this.trackChange(key, currVal, newVal);
          }
        }
      }
    });

    if (trackInitialValue) {
      Vue.set(this, key as string, initialValue);
    } else {
      Vue.set(this, underlyingVar, initialValue);
    }
  }

  //track changes
  get changes(): IChanges {
    return this._changes;
  }

  get hasChanged() {
    return this._hasChanged;
  }

  set hasChanged(newVal) {
    this._hasChanged = newVal;
    if (this.triggerChanges && this.parent && this.parent._track !== false) {
      this.parent.hasChanged = this.parent.hasChanged || newVal;
    }
  }

  get triggerChanges(): boolean {
    return this._triggerChanges;
  }

  set triggerChanges(newVal) {
    Logger.log('TrackedModel#autosave', 'debug', 'Setting triggerChanges', newVal, this)
    this._triggerChanges = newVal;
    if (newVal && !this.hasChanged) {
      Logger.log('TrackedModel#autosave', 'debug', 'Trigger changes not calling save because hasChanged is false', this)
    }
    if(newVal && this.hasChanged) {
      this.needsToSave();
    }
  }

  get ignoreChanges(): boolean {
    return this._ignoreChanges;
  }

  set ignoreChanges(newVal) {
    this._ignoreChanges = newVal;
  }

  get isAttached(): boolean {
    return !!this.parent;
  }

  resetChanges(): void {
    const embeddedModels = this.EMBEDDED_MODELS;
    const embeddedModelKeys = Object.keys(embeddedModels);

    for (const key in this.changes) {
      if (embeddedModelKeys.includes(key)) {
      } else if (key === '_destroy') {
        for (const client_id in this.changes._destroy) {
          const objChangesList = this.changes._destroy[client_id];
          for (const objChanges of objChangesList.reverse()) {
            if (embeddedModelKeys.includes(objChanges.parentRoot)) {
              if (objChanges.oldVal) {
                if (embeddedModels[objChanges.parentRoot].relationType === RelationType.singleObject) {
                  if (objChanges.isReplace) {
                    Vue.set(this, objChanges.parentRoot, objChanges.oldObject);
                  } else {
                    this[objChanges.parentRoot] = objChanges.object;
                  }
                } else {
                  Vue.set(this[objChanges.parentRoot], client_id, objChanges.object);
                }
                objChanges.object.parent = this;
                objChanges.object.addToRepo();
              } else {
                if (embeddedModels[objChanges.parentRoot].relationType === RelationType.singleObject) {
                  this[objChanges.parentRoot] = null;
                } else {
                  Vue.delete(this[objChanges.parentRoot], client_id);
                }
              }
            }
          }
        }
      } else {
        this[key] = this.changes[key].oldVal;
      }
    }

    for (const embeddedModelRelation in embeddedModels) {
      if (this[embeddedModelRelation]) {
        if (embeddedModels[embeddedModelRelation].relationType === RelationType.singleObject) {
          this[embeddedModelRelation].resetChanges();
        } else {
          for (const key in this[embeddedModelRelation]) {
            const object = this[embeddedModelRelation][key];
            if (object) {
              object.resetChanges();
            }
          }
        }
      }
    }

    this._hasChanged = false;
    this._changes = {};
  }

  needsToSave() {
    this.hasChanged = true;
  }

  allChanges() {
    Logger.log('TrackedModel#autosave', 'debug', 'All changes for:', this)
    const embeddedModels = this.EMBEDDED_MODELS;

    const result: any = { _model: this.constructor };
    if (!this.triggerChanges) {
      Logger.log('TrackedModel#autosave', 'debug', 'Returning due to triggerChanges false', this)
      return result;
    }
    if (this.id) {
      result.id = this.id;
    }
    if (this.client_id) {
      result.client_id = this.client_id;
    }

    for (const embeddedModelRelation in embeddedModels) {
      if (this[embeddedModelRelation]) {
        if (embeddedModels[embeddedModelRelation].relationType === RelationType.singleObject) {
          if (this[embeddedModelRelation].hasChanged) {
            Logger.log('TrackedModel#autosave', 'debug', 'Embedded single', embeddedModelRelation)
            result[embeddedModelRelation] = this[embeddedModelRelation].allChanges();
          }
        } else if (embeddedModels[embeddedModelRelation].relationType === RelationType.dictionary || embeddedModels[embeddedModelRelation].relationType === RelationType.array) {
          for (const key in this[embeddedModelRelation]) {
            const object = this[embeddedModelRelation][key];
            if (object.hasChanged) {
              result[embeddedModelRelation] = result[embeddedModelRelation] || [];
              Logger.log('TrackedModel#autosave', 'debug', 'Embedded dictionary', embeddedModelRelation)
              result[embeddedModelRelation].push(object.allChanges());
            }
          }
        } else { // collections
          for (const object of this[embeddedModelRelation].all) {
            if (object.hasChanged) {
              result[embeddedModelRelation] = result[embeddedModelRelation] || [];
              Logger.log('TrackedModel#autosave', 'debug', 'Embedded collection', embeddedModelRelation)
              result[embeddedModelRelation].push(object.allChanges());
            }
          }
        }
      }
    }

    Object.assign(result, this.changes);
    Logger.log('TrackedModel#autosave', 'debug', 'End Result', result)
    return result
  }

  clearChanges(): void {
    const embeddedModels = this.EMBEDDED_MODELS;

    this._hasChanged = false;
    this._changes = {};

    for (const embeddedModelRelation in embeddedModels) {
      if (this[embeddedModelRelation]) {
        if (embeddedModels[embeddedModelRelation].relationType === RelationType.singleObject) {
          this[embeddedModelRelation].clearChanges();
        } else if (embeddedModels[embeddedModelRelation].relationType === RelationType.collection) {
          this[embeddedModelRelation].all.forEach(model => {
            model.clearChanges && model.clearChanges();
          });
        } else {
          for (const key in this[embeddedModelRelation]) {
            this[embeddedModelRelation][key].clearChanges();
          }
        }
      }
    }
  }

  asJson(options: any = {}): any {
    const embeddedModels = this.EMBEDDED_MODELS;
    const embeddedModelKeys = Object.keys(embeddedModels);

    window.AppConfig.mergeJson(options, {except: this._defaultExcept}, {except: this.except});
    const json = {};
    const ownPropertyNames = Object.getOwnPropertyNames(this);
    for (let i = 0; i < ownPropertyNames.length; i++) {
      const propertyName = ownPropertyNames[i];
      if (this.JSON_IGNORE.includes(propertyName)) {
        // do nothing, ignore property
      } else if ((!options.except || !options.except.includes(propertyName)) && (!propertyName.startsWith('_') && !embeddedModelKeys.includes(propertyName))) {
        json[propertyName] = this[propertyName];
      } else if (embeddedModelKeys.includes(propertyName)) {
        const getPropKey = (withAttributes: boolean = false) => {
          if (options.forLoad || withAttributes) {
            return propertyName;
          } else {
            return `${propertyName}_attributes`;
          }
        }
        if (embeddedModels[propertyName].relationType === RelationType.singleObject) {
          if (this[propertyName]) {
            let attrPropName = getPropKey(this.EMBEDDED_HASHES.includes(propertyName))
            json[attrPropName] = this[propertyName].asJson(options);
          }
        } else if (embeddedModels[propertyName].relationType === RelationType.collection) {
          let attrPropName = getPropKey()
          json[attrPropName] = this[propertyName].asJson(options);
        } else {
          let attrPropName = getPropKey()
          json[attrPropName] = json[attrPropName] || [];
          for (const key in this[propertyName]) {
            json[attrPropName].push(this[propertyName][key].asJson(options));
          }
        }
      }
    }
    return json;
  }

  trackChange(key, oldVal, newVal) {
    if (this.ignoreChanges || this._track === false || key === "_type") {
      return;
    }

    // so rails know to empty the array
    if (Array.isArray(newVal) && newVal.length === 0) {
      newVal = [''];
    }
    if (Array.isArray(oldVal) && oldVal.length === 0) {
      oldVal = [''];
    }
    //
    if (this._changes[key]) {
      this._changes[key].newVal = newVal;
    } else {
      this._changes[key] = {oldVal: oldVal, newVal: newVal, object: this};
    }
    this.rootModel.lastPeekerOptions = {
      area: typeof (<any>this).currentBuilderArea === 'function' ? (<any>this).currentBuilderArea() : '',
      modelId: this.closestSelectableId || this.clientIdOrId,
    };
    this.hasChanged = true;
  }

  get closestSelectableId(): string {
    if ((<any>this).isSelectable || this.rootModel === this) {
      return this.clientIdOrId;
    } else if (this.parent) {
      return this.parent.closestSelectableId;
    } else {
      return null;
    }
  }

  destroy() {
    super.destroy();

    if (this.parent) {
      const currentJson = this.asJson();
      delete currentJson.id;
      if (this.isCollectionRoot) {
        Vue.delete(this.parent[this.nameInParent], this.clientIdOrId);
      } else {
        Vue.delete(this.parent, this.nameInParent);
      }
      // tracking must come after Vue.delete so hasChanged on root model gets called
      // with accurate data
      this.parent.trackCreationDeletion(this.clientIdOrId, currentJson, null, this);
      this.afterDestroy();
    }
  }

  // this really means something like 'propertyNameOnParent',
  // or nameInParent as below could be perfectly fine
  get rootName(): string {
    return null;
  }

  get rootModelDictName(): string {
    return null;
  }

  get nameInParent() {
    return this._nameInParent || this.rootName;
  }

  set nameInParent(value) {
    this._nameInParent = value;
  }

  get elementName(): string {
    return null;
  }

  // if parent -> child relationship is RelationshipType.singleObject
  // please override to false
  _isCollectionRoot: boolean
  // think about changing this to look on parent for what the collectionRoot type is
  get isCollectionRoot(): boolean {
    if (BooleanHelper.isStrictBoolean(this._isCollectionRoot)) {
      return this._isCollectionRoot
    } else {
      return true
    }
  }

  setCollectionRoot() {
    this._isCollectionRoot = this.parent.EMBEDDED_MODELS[this.rootName].relationType !== RelationType.singleObject;
  }

  get childRootName(): string {
    return null;
  }


  // Use these methods only when dealing with creating new objects on the front end.
  trackCreation(object: TrackedModel, setHasChanged: boolean = true): void {
    this.trackCreationDeletion(object.client_id, null, object.asJson(), object, setHasChanged);
  }

  trackDeletion(object: TrackedModel, setHasChanged: boolean = true): void {
    this.trackCreationDeletion(object.client_id, object.asJson(), null, object, setHasChanged);
  }

  // oldVal and newVal are JSON objects
  trackCreationDeletion(client_id: string, oldVal: any, newVal: any, object: TrackedModel, setHasChanged: boolean = true, oldObject: any = null): void {
    this._changes._destroy = this._changes._destroy || {};
    const parentRoot = object.rootName;

    if (!parentRoot) {
      throw new Error('This created/deleted model does not have a json root name');
    }
    const isReplace = oldObject && oldObject !== object
    this._changes._destroy[client_id] = this._changes._destroy[client_id] || [];
    this._changes._destroy[client_id].push({
      oldVal,
      newVal,
      object,
      parentRoot: object.rootName,
      isArray: object.isCollectionRoot,
      isReplace: isReplace,
      oldObject: oldObject
    });
    this.updateCreationDeletionIfNecessary()
    if (setHasChanged) {
      this.hasChanged = true;
    }
  }

  // should only be used on explicit creation of the model on the frontend
  // could cause problems if used when re-assigning an embedded child to a tracked model after the
  // base model has been loaded from the backend through the default .loadFromJson()
  attachToParent(parent: TrackedModel): void {
    this.rootModel = parent.rootModel;
    this.parent = parent;

    const oldObject = parent[this.rootName]

    this.attachToParentCallback();

    this.parent.placeChildrenInRelations(this, this.rootName, this.parent);

    if (this.childRootName) {
      const children = this[this.childRootName];
      for (const key in children) {
        const child = children[key];
        child.attachToParent(this);
      }
    }

    parent.trackCreationDeletion(this.client_id, oldObject?.asJson?.() || null, this.asJson(), this, true, oldObject);
  }

  attachToParentCallback() {}

  propObjectToArray(prop): Array<object>  {
    const values = this[prop]
    if (values) {
      return Object.keys(values)?.map(key => values[key]);
    } else {
      return null
    }
  }

  sortProperty(prop: string, sortBy: string = 'sort_order') {
    const values = this[prop]
    if (values) {
      let array;
      if (Array.isArray(values)) {
        array = values;
      } else {
        array = this.propObjectToArray(prop);
      }
      return array.sort((a, b) => a[sortBy] - b[sortBy]);
    } else {
      return null
    }
  }

  nextSortOrderFor(prop: string): number {
    const array = this.sortableArray(prop)
    if (array) {
      let position = 0;
      array.forEach((element, i) => {
        if (i >= position) {
          position = i + 1;
        }
      });
      return position;
    } else {
      return null
    }
  }

  sortableArray(prop: string): Array<object> {
    return this.propObjectToArray(prop)
  }

  updateSortOrder(prop: string, movedModel: ISortable = null, newPosition: number = null): void {
    const array = (<ISortable[]>this.sortableArray(prop));
    if (movedModel !== null && newPosition !== null) {
      array.filter(model => model !== movedModel)
          .sort((a, b) => a.sort_order - b.sort_order)
          .forEach((element, i) => {
            element.sort_order = i;
            if (element.sort_order >= newPosition) {
              element.sort_order += 1;
            }
          });
    } else {
      array.sort((a, b) => a.sort_order - b.sort_order)
          .forEach((element, i) => {
            element.sort_order = i;
          });
    }
  }

  placeInDictionary(record: ITrackedModel, rootName: string = record.rootName, parent: TrackedModel = null): void {
    record.runWithRepo((repo) => {
      repo.add(record);
    });
    Vue.set((parent || this)[rootName], record.clientIdOrId, record);
  }

  placeInKey(record: ITrackedModel, rootName: string = record.rootName, parent: TrackedModel = null): void {
    record.runWithRepo((repo) => {
      repo.add(record);
    });
    const relevantParent = parent || this
    Vue.set(relevantParent, rootName, record);
    if (relevantParent.afterPlace[rootName]) relevantParent.afterPlace[rootName].call(relevantParent)
  }

  placeChildrenInRelations(record: ITrackedModel, rootName: string = record.rootName, parent: TrackedModel = null): void {
    switch (this.EMBEDDED_MODELS[rootName].relationType) {
      case RelationType.singleObject:
        this.placeInKey(record, rootName, parent)
        break
      case RelationType.dictionary:
        this.placeInDictionary(record, rootName, parent)
        break
      default:
        Logger.errorOnce('placeChildrenInRelations', `RelationType "${this.EMBEDDED_MODELS[rootName].relationType}" not yet supported.`)
        break
    }
  }

  setKeys(keys) {
    for (const k in keys) {
      if (keys.hasOwnProperty(k)) {
        this[k] = keys[k];
      }
    }
  }

  findInPropImmediate<T>(key: string, id: string): T {
    return this[key].find(f => f.id === id || f.client_id === id);
  }

  findInProp<T>(key: string, id: string): T {
    let found = this.findInPropImmediate<T>(key, id);

    if (found) {
      return found;
    } else {
      for (let item of this[key]) {
        found = item.findInProp(key, id);
        if (found) {
          return found;
        }
      }
    }
  }

  findInPropOfType<T>(key: string, type: Constructor<T>, id: string): T {
    if (this instanceof type) {
      if (this.id === id || this.client_id === id) {
        return this;
      } else {
        return this.findInProp<T>(key, id);
      }
    }
  }

  initialize(rootModel: TrackedModel, parent: TrackedModel, type: number, json: object = {}): this {
    return this;
  }

  initializeAfterParentSet(): this {
    return this;
  }

  // Previously BuilderModel concerns

  currentBuilderArea(): string {
    // TODO: access router directly once Vue router is refactored to behave like singleton class
    return window.location.href.split('edit')[1];
  }

  queueSendChangesToServer(peekerOptions: string  | IPeekerOptions = {area: this.currentBuilderArea(), modelId: this.clientIdOrId}): void {
    window.CapybaraLockstep?.startWork('Autosave')
    this.cancelQueueSendChangesToServer();
    
    this.saveTimer = window.setTimeout(() => {
      this.saveType = this.AUTOSAVED_MESSAGE;
      this.sendChangesToServer(peekerOptions);
      this.saveTimer = null;
      window.CapybaraLockstep?.stopWork('Autosave')
    }, window.AppConfig.saveTimeout());
  }

  cancelQueueSendChangesToServer() {
    if (this.saveTimer) {
      window.CapybaraLockstep?.stopWork('Autosave')
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      return true;
    }
    return false;
  }

  saveToServer() {
    throw new Error('saveToServer has not been configured');
  }

  sendChangesToServer(peekerOptions: string | IPeekerOptions = {area: this.currentBuilderArea(), modelId: this.clientIdOrId}) {
    this.saveChangesToStore(peekerOptions);
    return this.saveToServer();
  }

  manualSave(peekerOptions: object = {area: this.currentBuilderArea()}) {
    this.cancelQueueSendChangesToServer();
    this.saveType = this.SAVED_MESSAGE;
    return this.sendChangesToServer();
  }

  saveChangesToStore(peekerOptions: string | IPeekerOptions = {area: this.currentBuilderArea(), modelId: this.clientIdOrId}): void {
    window.CapybaraLockstep?.startWork('saveChangesToStore')
    if (!this.hasChanged) {
      window.CapybaraLockstep?.stopWork('saveChangesToStore')
      return;
    }

    const changes = this.allChanges();
    this.clearChanges();
    this.saveChanges(changes, peekerOptions);
    this.lastPeekerOptions = null;
    window.CapybaraLockstep?.stopWork('saveChangesToStore')
  }

  handleErrorsAndUpdateSaveMessage(errors) {
    if (errors) {
      if (errors.client_idable_errors && errors.client_idable_errors.length > 0) {
        this.registerAutosaveError(errors);
        store.commit('flashStore/set', {
          flash: {
            alert: errors.client_idable_errors.join("/n")
          },
          mode: FlashMode.Modal,
          flashDetails: {
            buttons: [
              {
                action: () => {
                  window.location.reload()
                },
                class: "btn blue",
                text: "Refresh Page"
              }
            ]
          }
        });
        return;
      } else {
        let otherErrors = Object.keys(errors).filter(e => e !== "client_idable_errors")
        if (otherErrors.length > 0) {
          this.registerAutosaveError();
          store.commit('flashStore/set', {
            flash: {
              alert: Object.values(otherErrors).join(", ")
            }
          });
          return;
        }
      }
    }
    this.u_at_moment = moment();
    this.updateLastSaveMessage();
  }

  registerAutosaveError(err?: string) {
    this.u_at_moment = moment();
    this.saveType = this.AUTOSAVE_FAILED_MESSAGE
    this.updateLastSaveMessage();
  }

  updateLastSaveMessage() {
    this.lastSavedMessage = `${ this.saveType } ${ this.u_at_moment.fromNow() }`;

    const secondsSince = moment().diff(this.u_at_moment, 'seconds');
    let timeout = 60000;
    if (secondsSince <= 600) {
    } else if (secondsSince <= 3600) {
      timeout = 300000;
    } else {
      timeout = 3600000;
    }

    if (this.updateLastSavedMessageTimer) {
      clearTimeout(this.updateLastSavedMessageTimer);
    }
    this.updateLastSavedMessageTimer = setTimeout(() => {
      this.updateLastSaveMessage();
    }, timeout);
  }

  protected saveChanges(changes: any, peekerOptions: string | IPeekerOptions): void {
    throw new Error('saveChanges has not been configured');
  }

  private get SAVING_MESSAGE(): string {
    return 'saving';
  }

  private get SAVED_MESSAGE(): string {
    return 'saved';
  }

  private get AUTOSAVED_MESSAGE(): string {
    return 'Autosaved';
  }

  private get AUTOSAVE_FAILED_MESSAGE(): string {
    return 'Autosave error occured';
  }

  withoutTracking(callback: (...args) => void): void {
    const originalTrack = this._track;
    this._track = false;
    callback();
    this._track = originalTrack;
  }

  stopTracking() {
    this._track = false
  }

  startTracking() {
    this._track = true
  }

  defineClientId(val: string = null) {
    this.defineProperty('client_id', val || Guid.create())
  }

  // by @jack
  // fixes bug with embedded children having trackCreationDeletion called after parent gets called
  // will let the parent know about new creations
  // so overrides when parsing changes
  // wont result in lost data eg
  // parent is loaded from json
  // child is created and trackCreationDeletion called
  // sub-child (of child) is created and trackCreationDeletion called
  // ensures data sent to server will include the sub-child's creation data.
  updateCreationDeletionIfNecessary() {
    if (this.parent &&
      this.parent.changes._destroy &&
      this.parent.changes._destroy[this.client_id]) {
      const destroys = this.parent.changes._destroy[this.client_id];
      const lastDestroy = destroys[destroys.length - 1];
      if (lastDestroy.newVal) {
        lastDestroy.newVal = this.asJson();
        this.parent.updateCreationDeletionIfNecessary();
      } else {
        lastDestroy.oldVal = this.asJson();
        this.parent.updateCreationDeletionIfNecessary();
      }
    }
  }

  // override as needed
  addToRepo(): void {
    this.repo.add(this);
  }

  static createTrackedArrayProxy(key: PropertyKey, arr: Array<any> = [], parent: any, options: any = {}) {
    const proxyTarget = Object.assign([], arr, { parent: parent, key: key })
    return TrackedArrayProxy.create(proxyTarget)
  }
}
